import dayjs from 'dayjs';
import { SortDirection } from '~/modules/covenants/types.ts';
import type {
  TBuildManagerApplicationsUrlSearchParams,
  TManagerApplicationSortKey,
  TManagerApplicationsUrlSyncedFilters,
  TManagerApplicationsUrlState,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import {
  DEFAULT_MANAGER_APPLICATION_FILTERS,
  MANAGER_APPLICATION_ROWS_PER_PAGE_OPTIONS,
  MANAGER_APPLICATION_SORTABLE_COLUMNS,
  MANAGER_APPLICATIONS_URL_MAX_SEARCH_LENGTH,
  MANAGER_APPLICATIONS_URL_PARAMS,
  MANAGER_ASSIGNEE_COLUMN,
  MANAGER_STATUS_COLUMN,
  MANAGER_STATUS_OPTIONS,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';

const MANAGER_ALLOWED_SORT_KEYS: TManagerApplicationSortKey[] = [
  ...MANAGER_APPLICATION_SORTABLE_COLUMNS.map((column) => column.sortKey),
  MANAGER_STATUS_COLUMN.sortKey,
  MANAGER_ASSIGNEE_COLUMN.sortKey,
];
const MANAGER_ALLOWED_STATUSES = new Set<string>(MANAGER_STATUS_OPTIONS);

const parseNonNegativeNumber = (value: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsedValue = Number.parseInt(value, 10);

  return Number.isInteger(parsedValue) && parsedValue >= 0 ? parsedValue : null;
};

const parseManagerDateFromQuery = (value: string | null, mode: 'start' | 'end') => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const parsedDate = dayjs(value);

  if (!parsedDate.isValid()) {
    return null;
  }

  return mode === 'start' ? parsedDate.startOf('day') : parsedDate.endOf('day');
};

const parseManagerSortKeyFromQuery = (
  value: string | null
): TManagerApplicationsUrlSyncedFilters['sortKey'] => {
  if (!value) {
    return null;
  }

  return MANAGER_ALLOWED_SORT_KEYS.includes(value as TManagerApplicationSortKey)
    ? (value as TManagerApplicationSortKey)
    : null;
};

const parseManagerSortDirectionFromQuery = (
  value: string | null
): TManagerApplicationsUrlSyncedFilters['sortDirection'] | null => {
  if (
    value === SortDirection.None ||
    value === SortDirection.Asc ||
    value === SortDirection.Desc
  ) {
    return value;
  }

  return null;
};

const normalizeUniqueStrings = (values: string[]): string[] => {
  const uniqueValues = new Set<string>();

  values.forEach((value) => {
    const normalizedValue = value.trim();

    if (!normalizedValue || uniqueValues.has(normalizedValue)) {
      return;
    }

    uniqueValues.add(normalizedValue);
  });

  return [...uniqueValues];
};

const normalizeStatusFilter = (
  values: string[]
): TManagerApplicationsUrlSyncedFilters['status'] =>
  normalizeUniqueStrings(values).filter(
    (status): status is TManagerApplicationsUrlSyncedFilters['status'][number] =>
      MANAGER_ALLOWED_STATUSES.has(status)
  );

const normalizeAssigneeFilter = (
  values: string[]
): TManagerApplicationsUrlSyncedFilters['assignee'] => normalizeUniqueStrings(values);

const normalizeSegmentFilter = (
  values: string[]
): TManagerApplicationsUrlSyncedFilters['segmentIds'] => normalizeUniqueStrings(values);

const resolveManagerSortStateFromQuery = (searchParams: URLSearchParams) => {
  const parsedSortKey = parseManagerSortKeyFromQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.sortKey)
  );
  const parsedSortDirection = parseManagerSortDirectionFromQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.sortDirection)
  );

  if (parsedSortDirection === SortDirection.None && !parsedSortKey) {
    return {
      sortKey: null,
      sortDirection: SortDirection.None,
    };
  }

  if (parsedSortKey) {
    return {
      sortKey: parsedSortKey,
      sortDirection: parsedSortDirection ?? DEFAULT_MANAGER_APPLICATION_FILTERS.sortDirection,
    };
  }

  return {
    sortKey: DEFAULT_MANAGER_APPLICATION_FILTERS.sortKey,
    sortDirection: parsedSortDirection ?? DEFAULT_MANAGER_APPLICATION_FILTERS.sortDirection,
  };
};

const getUrlSafeSearchQuery = (value: string) =>
  value.trim().slice(0, MANAGER_APPLICATIONS_URL_MAX_SEARCH_LENGTH);

const replaceMultiValueParam = (searchParams: URLSearchParams, key: string, values: string[]) => {
  searchParams.delete(key);
  values.forEach((value) => {
    searchParams.append(key, value);
  });
};

export const parseManagerApplicationsUrlState = (search: string): TManagerApplicationsUrlState => {
  const searchParams = new URLSearchParams(search);
  const parsedPage = parseNonNegativeNumber(searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.page));
  const parsedRowsPerPage = parseNonNegativeNumber(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.pageSize)
  );
  const parsedSearchQuery = getUrlSafeSearchQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.search) ?? ''
  );
  const parsedStartDate = parseManagerDateFromQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.fromDate),
    'start'
  );
  const parsedEndDate = parseManagerDateFromQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.toDate),
    'end'
  );
  const parsedStatus = normalizeStatusFilter(
    searchParams.getAll(MANAGER_APPLICATIONS_URL_PARAMS.status)
  );
  const parsedAssignee = normalizeAssigneeFilter(
    searchParams.getAll(MANAGER_APPLICATIONS_URL_PARAMS.assignee)
  );
  const parsedSegmentIds = normalizeSegmentFilter(
    searchParams.getAll(MANAGER_APPLICATIONS_URL_PARAMS.segmentIds)
  );
  const sortStateFromQuery = resolveManagerSortStateFromQuery(searchParams);
  const isRowsPerPageAllowed = MANAGER_APPLICATION_ROWS_PER_PAGE_OPTIONS.includes(
    parsedRowsPerPage ?? -1
  );
  const normalizedStartDate = parsedStartDate;
  let normalizedEndDate = parsedEndDate;

  if (normalizedStartDate && normalizedEndDate && normalizedStartDate.isAfter(normalizedEndDate)) {
    normalizedEndDate = normalizedStartDate.endOf('day');
  }

  return {
    page: parsedPage ?? DEFAULT_MANAGER_APPLICATION_FILTERS.page,
    rowsPerPage: isRowsPerPageAllowed
      ? (parsedRowsPerPage as number)
      : DEFAULT_MANAGER_APPLICATION_FILTERS.rowsPerPage,
    searchQuery: parsedSearchQuery,
    startDate: normalizedStartDate,
    endDate: normalizedEndDate,
    sortKey: sortStateFromQuery.sortKey,
    sortDirection: sortStateFromQuery.sortDirection,
    status: parsedStatus,
    assignee: parsedAssignee,
    segmentIds: parsedSegmentIds,
  };
};

export const buildManagerApplicationsUrlSearch = ({
  currentSearch,
  page,
  rowsPerPage,
  submittedSearchQuery,
  startDate,
  endDate,
  sortKey,
  sortDirection,
  status,
  assignee,
  segmentIds,
}: TBuildManagerApplicationsUrlSearchParams) => {
  const searchParams = new URLSearchParams(currentSearch);
  const normalizedSearchQuery = getUrlSafeSearchQuery(submittedSearchQuery);
  const pageValue = String(Math.max(0, page));
  const rowsPerPageValue = String(rowsPerPage);
  const fromDate = startDate?.format('YYYY-MM-DD') ?? '';
  const toDate = endDate?.format('YYYY-MM-DD') ?? '';
  const normalizedStatus = normalizeStatusFilter(status);
  const normalizedAssignee = normalizeAssigneeFilter(assignee);
  const normalizedSegmentIds = normalizeSegmentFilter(segmentIds);

  if (page > 0) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.page, pageValue);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.page);
  }

  if (rowsPerPage !== DEFAULT_MANAGER_APPLICATION_FILTERS.rowsPerPage) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.pageSize, rowsPerPageValue);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.pageSize);
  }

  if (normalizedSearchQuery) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.search, normalizedSearchQuery);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.search);
  }

  if (fromDate) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.fromDate, fromDate);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.fromDate);
  }

  if (toDate) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.toDate, toDate);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.toDate);
  }

  if (sortKey && sortKey !== DEFAULT_MANAGER_APPLICATION_FILTERS.sortKey) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.sortKey, sortKey);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.sortKey);
  }

  if (sortDirection === SortDirection.None) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.sortDirection, sortDirection);
  } else if (sortDirection !== DEFAULT_MANAGER_APPLICATION_FILTERS.sortDirection) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.sortDirection, sortDirection);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.sortDirection);
  }

  if (normalizedStatus.length) {
    replaceMultiValueParam(searchParams, MANAGER_APPLICATIONS_URL_PARAMS.status, normalizedStatus);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.status);
  }

  if (normalizedAssignee.length) {
    replaceMultiValueParam(
      searchParams,
      MANAGER_APPLICATIONS_URL_PARAMS.assignee,
      normalizedAssignee
    );
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.assignee);
  }

  if (normalizedSegmentIds.length) {
    replaceMultiValueParam(
      searchParams,
      MANAGER_APPLICATIONS_URL_PARAMS.segmentIds,
      normalizedSegmentIds
    );
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.segmentIds);
  }

  return searchParams.toString();
};

export const getManagerApplicationsSearchQueryForUrl = getUrlSafeSearchQuery;
